/**
 * Variant Analysis Section — Shows textual differences between
 * TR (KJV) and WH (critical text) from the variant-analysis.sqlite DB.
 *
 * Deterministic data — no LLM needed.
 */

import type { VariantAnalysis } from '@skjv/common';
import { InlineStrongsChip } from './inline-strongs-chip';

interface VariantAnalysisSectionProps {
  variant: VariantAnalysis;
}

const IMPACT_CLASSES: Record<string, string> = {
  high: 'variant-impact-high',
  medium: 'variant-impact-medium',
  low: 'variant-impact-low',
};

export function VariantAnalysisSection({ variant }: VariantAnalysisSectionProps) {
  return (
    <div className="rich-message-section">
      <h4 className="rich-message-section-title">
        Textual Variant — {variant.reference}
        <span className={`variant-badge ${IMPACT_CLASSES[variant.impact] ?? ''}`}>
          {variant.impact} impact
        </span>
      </h4>

      <p className="variant-summary">{variant.shortSummary}</p>

      {variant.numberSummary && (
        <p className="variant-detail variant-number-summary">{variant.numberSummary}</p>
      )}

      {variant.onlyInKjv.length > 0 && (
        <p className="variant-detail">
          <span className="variant-label">Only in KJV (TR):</span>{' '}
          {variant.onlyInKjv.map((s, i) => (
            <span key={s}>
              {i > 0 && ', '}
              <InlineStrongsChip number={s} displayText={s} />
            </span>
          ))}
        </p>
      )}

      {variant.onlyInNasb.length > 0 && (
        <p className="variant-detail">
          <span className="variant-label">Only in NASB (WH):</span>{' '}
          {variant.onlyInNasb.map((s, i) => (
            <span key={s}>
              {i > 0 && ', '}
              <InlineStrongsChip number={s} displayText={s} />
            </span>
          ))}
        </p>
      )}

      {variant.affectedConcepts.length > 0 && (
        <p className="variant-concepts">
          Affected concepts: {variant.affectedConcepts.join(', ')}
        </p>
      )}

      {variant.isHumanReviewed && (
        <span className="variant-reviewed-badge">Reviewed</span>
      )}
    </div>
  );
}
